/**
 * Pure presentation and routing rules for deployment and release history.
 *
 * The Console has no DOM test harness. Keeping these decisions here makes the
 * safety boundary (external versus hash-router links), URL-backed list state,
 * and malformed-wire fallbacks executable without pretending to render React
 * in Node.
 */

import type { Tone } from './display-matrix.js';
import { formatDurationMs, formatTimestamp } from './format.js';
import type {
  BoardReleaseOperationView,
  ReleaseConfigurationView,
  ReleaseOperationView,
  ReleasePageView,
} from '../client/types.js';

export type ReleaseCollection = 'deployments' | 'releases';
export type ReleaseListMode = ReleaseCollection;
export type ReleaseQueryPhase = 'loading' | 'error' | 'empty' | 'ready';

export interface ReleasePageMetadata {
  readonly limit: number;
  readonly offset: number;
  readonly returned: number;
}

const KNOWN_STATUSES = new Set([
  'DRAFT',
  'PREPARING',
  'STARTED',
  'RUNNING',
  'VERIFYING',
  'DEPLOYING',
  'SUCCEEDED',
  'RELEASED',
  'FAILED',
  'CANCELLED',
  'UNKNOWN',
]);

const ACTIVE_STATUSES = new Set(['PREPARING', 'STARTED', 'RUNNING', 'VERIFYING', 'DEPLOYING']);
const SUCCESS_STATUSES = new Set(['SUCCEEDED', 'RELEASED']);

function canonicalOffset(value: string | undefined): number {
  if (value === undefined || !/^(?:0|[1-9][0-9]*)$/u.test(value)) return 0;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : 0;
}

export function parseReleaseListState(params: ReadonlyMap<string, string>): {
  readonly mode: ReleaseListMode;
  readonly offset: number;
} {
  return {
    mode: params.get('view') === 'deployments' ? 'deployments' : 'releases',
    offset: canonicalOffset(params.get('offset')),
  };
}

/** React Router path for one linkable list selection/page. */
export function releaseListHref(mode: ReleaseListMode, offset: number): string {
  const safeOffset = Number.isSafeInteger(offset) && offset >= 0 ? offset : 0;
  return `/releases?view=${mode}&offset=${String(safeOffset)}`;
}

/** Detail route that carries its originating list selection for a useful Back link. */
export function releaseDetailHref(
  kind: 'deployment' | 'release',
  id: string,
  returnState: { readonly mode: ReleaseListMode; readonly offset: number },
): string {
  const collection = kind === 'deployment' ? 'deployments' : 'releases';
  const query = releaseListHref(returnState.mode, returnState.offset).slice('/releases'.length);
  return `/${collection}/${encodeURIComponent(id)}${query}`;
}

/** Stable identity for focus management; query result objects are deliberately absent. */
export function releaseDetailFocusKey(kind: 'deployment' | 'release', id: string | null): string {
  return `${kind}:${id === null ? '(missing)' : id}`;
}

/** Focus only after a terminal target exists, and only when route identity changes. */
export function shouldFocusReleaseDetail(
  lastFocusedKey: string | null,
  routeKey: string,
  targetAvailable: boolean,
): boolean {
  return targetAvailable && lastFocusedKey !== routeKey;
}

/** Control API collection path; the common client appends the live team query. */
export function releaseListApiPath(collection: ReleaseCollection, page: { readonly limit: number; readonly offset: number }): string {
  return `/v1/${collection}?limit=${encodeURIComponent(String(page.limit))}&offset=${encodeURIComponent(String(page.offset))}`;
}

/** Control API detail path; identifiers are path segments, never URL fragments. */
export function releaseDetailApiPath(collection: ReleaseCollection, id: string): string {
  return `/v1/${collection}/${encodeURIComponent(id)}`;
}

/** Team identity belongs in cache keys so switching teams cannot reuse stale data. */
export function releaseListQueryKey(
  collection: ReleaseCollection,
  teamId: string,
  page: { readonly limit: number; readonly offset: number },
): readonly [ReleaseCollection, string, number, number] {
  return [collection, teamId, page.limit, page.offset];
}

export function releaseDetailQueryKey(
  collection: ReleaseCollection,
  teamId: string,
  id: string | null,
): readonly ['deployment' | 'release', string, string | null] {
  return [collection === 'deployments' ? 'deployment' : 'release', teamId, id];
}

/** The API does not expose a total; a full page is the only honest next-page signal. */
export function releaseListPagination(page: ReleasePageMetadata): {
  readonly previousOffset: number | null;
  readonly nextOffset: number | null;
} {
  const limit = Number.isSafeInteger(page.limit) && page.limit > 0 ? page.limit : 1;
  const offset = Number.isSafeInteger(page.offset) && page.offset >= 0 ? page.offset : 0;
  const returned = Number.isSafeInteger(page.returned) && page.returned >= 0 ? page.returned : 0;
  return {
    previousOffset: offset === 0 ? null : Math.max(0, offset - limit),
    nextOffset: returned === limit ? offset + limit : null,
  };
}

export function releaseQueryPhase(input: {
  readonly isPending: boolean;
  readonly error: unknown;
  readonly itemCount: number;
}): ReleaseQueryPhase {
  if (input.isPending) return 'loading';
  if (input.error !== null && input.error !== undefined) return 'error';
  return input.itemCount === 0 ? 'empty' : 'ready';
}

/** Status is always a word. A future or malformed value is uncertainty, not a new claim. */
export function releaseStatusPresentation(status: unknown): { readonly text: string; readonly tone: Tone } {
  if (typeof status !== 'string' || !KNOWN_STATUSES.has(status)) return { text: 'UNKNOWN', tone: 'unknown' };
  if (status === 'FAILED') return { text: status, tone: 'danger' };
  if (status === 'UNKNOWN') return { text: status, tone: 'unknown' };
  if (status === 'CANCELLED' || status === 'DRAFT') return { text: status, tone: 'neutral' };
  if (SUCCESS_STATUSES.has(status)) return { text: status, tone: 'active' };
  if (ACTIVE_STATUSES.has(status)) return { text: status, tone: 'active' };
  return { text: status, tone: 'info' };
}

/** Long values are preserved for wrapping/copying; absent or malformed values say so. */
export function releaseText(value: unknown, fallback = 'Unavailable'): string {
  return typeof value === 'string' && value.trim().length > 0 ? value : fallback;
}

export function releaseTimestampPresentation(value: unknown): { readonly dateTime: string | null; readonly text: string } {
  if (typeof value !== 'string' || !Number.isFinite(Date.parse(value))) return { dateTime: null, text: 'Unavailable' };
  return { dateTime: value, text: formatTimestamp(value) };
}

export function releaseDurationText(value: unknown): string {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? formatDurationMs(value) : 'Unavailable';
}

/**
 * Attributes for an external release link. Only HTTP(S) is navigable; provider
 * mistakes such as file:// or javascript: become explicit non-links.
 */
export function externalReleaseLinkProps(value: unknown): {
  readonly href: string;
  readonly target: '_blank';
  readonly rel: 'noopener noreferrer';
} | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    if (
      (url.protocol !== 'https:' && url.protocol !== 'http:') ||
      url.hostname.length === 0 ||
      url.username.length > 0 ||
      url.password.length > 0
    ) return null;
    return { href: value, target: '_blank', rel: 'noopener noreferrer' };
  } catch {
    return null;
  }
}

function record(value: unknown): Readonly<Record<string, unknown>> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Readonly<Record<string, unknown>>)
    : null;
}

/** Rejects broken aggregate structure before components dereference nested fields. */
export function safeReleaseOperationView(value: unknown): ReleaseOperationView | null {
  const operation = record(value);
  const deployment = record(operation?.['deployment']);
  const target = record(deployment?.['target']);
  const release = operation?.['release'];
  const releaseRecord = release === null ? null : record(release);
  const items = operation?.['items'];
  const attempts = operation?.['attempts'];
  if (
    operation === null ||
    deployment === null ||
    target === null ||
    typeof deployment['id'] !== 'string' ||
    deployment['id'].length === 0 ||
    (release !== null &&
      (releaseRecord === null || typeof releaseRecord['id'] !== 'string' || releaseRecord['id'].length === 0)) ||
    !Array.isArray(items) ||
    !Array.isArray(attempts) ||
    items.some((item) => record(item) === null || record(record(item)?.['task']) === null) ||
    attempts.some((attempt) => record(attempt) === null)
  ) {
    return null;
  }
  return value as ReleaseOperationView;
}

/** Validates the list envelope and every aggregate; malformed API data is an error state. */
export function safeReleasePageView(value: unknown): ReleasePageView | null {
  const page = record(value);
  const items = page?.['items'];
  const pagination = record(page?.['pagination']);
  if (!Array.isArray(items) || pagination === null) return null;
  const limit = pagination['limit'];
  const offset = pagination['offset'];
  const returned = pagination['returned'];
  if (
    typeof limit !== 'number' ||
    !Number.isSafeInteger(limit) ||
    limit <= 0 ||
    typeof offset !== 'number' ||
    !Number.isSafeInteger(offset) ||
    offset < 0 ||
    typeof returned !== 'number' ||
    !Number.isSafeInteger(returned) ||
    returned < 0 ||
    items.some((item) => safeReleaseOperationView(item) === null)
  ) {
    return null;
  }
  return value as ReleasePageView;
}

/** Filters individual malformed card projection entries without hiding valid siblings. */
export function safeBoardReleaseOperations(value: unknown): readonly BoardReleaseOperationView[] {
  if (!Array.isArray(value)) return [];
  return value.filter((candidate): candidate is BoardReleaseOperationView => {
    const operation = record(candidate);
    const target = record(operation?.['target']);
    return (
      operation !== null &&
      target !== null &&
      typeof operation['deploymentId'] === 'string' &&
      operation['deploymentId'].length > 0 &&
      (operation['releaseId'] === null || typeof operation['releaseId'] === 'string') &&
      typeof operation['title'] === 'string' &&
      typeof operation['status'] === 'string' &&
      typeof operation['createdAt'] === 'string' &&
      typeof operation['updatedAt'] === 'string' &&
      typeof operation['deploymentPath'] === 'string' &&
      (operation['releasePath'] === null || typeof operation['releasePath'] === 'string')
    );
  });
}

/** API-supplied board paths belong to the hash router, not to the open web. */
export function safeReleaseRoutePath(value: unknown, collection: ReleaseCollection): string | null {
  if (typeof value !== 'string') return null;
  const prefix = `/${collection}/`;
  if (!value.startsWith(prefix)) return null;
  const id = value.slice(prefix.length);
  return id.length > 0 && !/[/?#\\]/u.test(id) ? value : null;
}

/** The board projection is newest-first; sort defensively without mutating query data. */
export function sortBoardReleaseOperations<T extends { readonly deploymentId: string; readonly createdAt: string }>(
  operations: readonly T[],
): readonly T[] {
  return [...operations].sort(
    (left, right) =>
      right.createdAt.localeCompare(left.createdAt) || right.deploymentId.localeCompare(left.deploymentId),
  );
}

type ReleaseEligibility = Pick<ReleaseConfigurationView, 'readyStage' | 'environments'>;

/** Stages whose tasks may be deployed to one environment; mirrors the Control API. */
export function releaseEnvironmentReadyStages(configuration: ReleaseEligibility | undefined, environmentId: string): readonly string[] {
  if (configuration === undefined) return [];
  const configured = configuration.environments.find((environment) => environment.id === environmentId)?.readyStages;
  if (configured !== undefined) return configured;
  return configuration.readyStage === null ? [] : [configuration.readyStage];
}

/** Stages whose tasks may join a draft release: eligible for at least one environment. */
export function releaseDraftReadyStages(configuration: ReleaseEligibility | undefined): readonly string[] {
  if (configuration === undefined) return [];
  if (configuration.environments.length === 0) return configuration.readyStage === null ? [] : [configuration.readyStage];
  return [...new Set(configuration.environments.flatMap((environment) => releaseEnvironmentReadyStages(configuration, environment.id)))];
}

/** A task position may be added to a draft release; completed stage work never is. */
export function isReleaseDraftEligible(
  configuration: ReleaseEligibility | undefined,
  position: { readonly stageId: string; readonly phase: string } | null | undefined,
): boolean {
  return position != null && position.phase !== 'done' && releaseDraftReadyStages(configuration).includes(position.stageId);
}

/** Release records are updated by the shared event stream; unrelated frames do nothing. */
export function releaseEventInvalidates(eventType: string): boolean {
  return eventType === 'snapshot' || eventType.startsWith('deployment.') || eventType.startsWith('release.');
}
